import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export interface DatabricksNotebookImport {
  path: string;
  content: string | Uint8Array;
  language?: "PYTHON" | "SCALA" | "SQL" | "R";
  format?: "SOURCE" | "JUPYTER" | "HTML" | "DBC" | "AUTO";
  overwrite?: boolean;
}
export class DatabricksWorkspaceAuthoring {
  constructor(private readonly client: DatabricksRawProtocolClient) {}
  importNotebook(input: DatabricksNotebookImport): Promise<unknown> {
    return this.client.post(
      "/api/2.0/workspace/import",
      {
        path: input.path,
        content: base64(input.content),
        format: input.format ?? "AUTO",
        ...(input.language ? { language: input.language } : {}),
        overwrite: input.overwrite ?? false,
      },
      {},
      { retry: "never" },
    );
  }
  deleteWorkspaceObject(path: string, recursive = false): Promise<unknown> {
    return this.client.post(
      "/api/2.0/workspace/delete",
      { path, recursive },
      {},
      { retry: "never" },
    );
  }
  mkdirs(path: string): Promise<unknown> {
    return this.client.post("/api/2.0/workspace/mkdirs", { path }, {}, { retry: "safe" });
  }
}
export function databricksWorkspaceAuthoringTools(client: DatabricksRawProtocolClient): ToolDef[] {
  const api = new DatabricksWorkspaceAuthoring(client);
  const pathResource = [{ kind: "workspace-object", path: "/path" }];
  return [
    tool("databricks_import_notebook", importSchema(), pathResource, (i) =>
      api.importNotebook(i as unknown as DatabricksNotebookImport),
    ),
    tool(
      "databricks_delete_workspace_object",
      {
        type: "object",
        additionalProperties: false,
        properties: { path: { type: "string" }, recursive: { type: "boolean" } },
        required: ["path"],
      },
      pathResource,
      (i) => api.deleteWorkspaceObject(i.path as string, i.recursive as boolean | undefined),
    ),
    tool("databricks_workspace_mkdirs", pathSchema(), pathResource, (i) =>
      api.mkdirs(i.path as string),
    ),
  ];
}
function tool(
  name: string,
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "workspace",
      governance: { resources },
    },
    execute,
  };
}
function pathSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { path: { type: "string" } },
    required: ["path"],
  };
}
function importSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      path: { type: "string" },
      content: { type: "string" },
      language: { type: "string" },
      format: { type: "string" },
      overwrite: { type: "boolean" },
    },
    required: ["path", "content"],
  };
}
function base64(value: string | Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
    return Buffer.from(
      bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
    ).toString("base64");
  }
  const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
  return btoa(String.fromCharCode(...bytes));
}
